/* eslint-disable react/style-prop-object */
import _ from 'lodash';
import {Field, FieldProps, useField, useFormikContext} from 'formik';
import React, {useState} from 'react';
import {IconButton} from '@material-ui/core';
import {useIntl} from 'react-intl';
import {RootState} from '../../../store';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from '../../../base-components/StudioToast';
import './DeploymentDetails.scss';
import {StreamingDevice} from '../../../types/deployment/StreamingDeviceResponse';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {fetchExternalStreamingDevices} from '../../../store/prodDeployments/create-deployment';
import {DashboardNodeEntry} from '../../../api/deployment/DeploymentNodes';
import {deleteDevice} from '../../../api/device/DeviceList';
import {Clear} from '@material-ui/icons';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import cn from 'classnames';
import {InputChoice, InputFormValues, SetUp} from './DeploymentDetails';

type InputFieldParameters = {
  disabled: boolean;
  setup: SetUp;
  setSetup: React.Dispatch<React.SetStateAction<SetUp>>;
};

const InputSourceTypeOptions: Record<InputChoice, string> = {
  DEVICE: 'On Device',
  EXTERNAL: 'External',
};

export const InputSourceTypeField = ({
  disabled,
  setup,
  setSetup,
}: InputFieldParameters) => {
  const intl = useIntl();
  const {values, setFieldValue} = useFormikContext<InputFormValues>();

  return (
    <Field name="inputSourceType">
      {({field, meta}: FieldProps<InputFormValues['inputSourceType']>) => (
        <StudioSelect
          className={cn(
            values.inputSourceType
              ? 'create-prod-deploy__select__selected'
              : 'create-prod-deploy__select__unselected'
          )}
          dataTestId="create-prod-deploy-source-type"
          label={intl.formatMessage({
            id: 'createDeploy.inputSourceType',
          })}
          placeholder={intl.formatMessage({
            id: 'createDeploy.selectSource',
          })}
          disablePlaceholder
          options={InputSourceTypeOptions}
          SelectProps={{
            ...field,
            open: setup.srcType,
            onOpen: () => setSetup({...setup, srcType: true}),
            onClose: () => setSetup({...setup, srcType: false}),
            value: field.value === null ? '' : field.value,
            onChange: event => {
              if (values.inputSourceType !== event.target.value) {
                setFieldValue('inputSourceType', event.target.value);
                setFieldValue('externalSourceType', '');
                setFieldValue('inputSource', '');
              }
            },
          }}
          error={Boolean(meta.error && meta.touched)}
          helperText={meta.error && meta.touched ? meta.error : ''}
          disabled={disabled}
          required
        />
      )}
    </Field>
  );
};

export const ExternalSourceTypeField = ({
  disabled,
  setup,
  setSetup,
}: InputFieldParameters) => {
  const intl = useIntl();
  const {values, setFieldValue} = useFormikContext<InputFormValues>();
  const selectedDeployment = useSelector(
    (state: RootState) => state.deploymentDashboard.selectedDeployment
  );

  // these 3 are unavailable on this screen
  const {
    // @ts-ignore
    TEXT_INPUT,
    // @ts-ignore
    WEB_CAMERA,
    // @ts-ignore
    IMAGES,
    ...extSrcTypOptions
  } = selectedDeployment?.supportedExternalInputSources;

  return selectedDeployment && values.inputSourceType === 'EXTERNAL' ? (
    <Field name="externalSourceType">
      {({field, meta}: FieldProps<InputFormValues['externalSourceType']>) => (
        <StudioSelect
          className={cn(
            values.externalSourceType
              ? 'create-prod-deploy__select__selected'
              : 'create-prod-deploy__select__unselected'
          )}
          dataTestId="create-prod-deploy-external-source-type"
          label={intl.formatMessage({id: 'createDeploy.externalSourceType'})}
          placeholder={intl.formatMessage({
            id: 'createDeploy.selectSource',
          })}
          disablePlaceholder
          options={extSrcTypOptions}
          SelectProps={{
            ...field,
            open: setup.extType,
            onOpen: () => setSetup({...setup, extType: true}),
            onClose: () => setSetup({...setup, extType: false}),
            value: field.value === null ? '' : field.value,
            onChange: event => {
              if (values.externalSourceType !== event.target.value) {
                setFieldValue('externalSourceType', event.target.value);
                setFieldValue('inputSource', '');
              }
            },
          }}
          error={Boolean(meta.error && meta.touched)}
          helperText={meta.error && meta.touched ? meta.error : ''}
          disabled={disabled}
          required
        />
      )}
    </Field>
  ) : null;
};

type InputSourceFieldParameters = InputFieldParameters & {
  nodes: Array<DashboardNodeEntry>;
  externalStreamingDevices: Array<StreamingDevice>;
};

export const InputSourceField = ({
  nodes,
  externalStreamingDevices,
  disabled,
  setup,
  setSetup,
}: InputSourceFieldParameters) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const {
    values: {inputSourceType, externalSourceType},
    setFieldValue,
  } = useFormikContext<InputFormValues>();
  const selectedDeployment = useSelector(
    (state: RootState) => state.deploymentDashboard.selectedDeployment
  );

  const [confirmDeleteDevice, setConfirmDeleteDevice] = useState<{
    isOpen: boolean;
    id: string | null;
  }>({isOpen: false, id: null});

  // const inputSourceTypeRef = useRef(inputSourceType);
  const [field, meta] = useField('inputSource');

  const onDeviceSources =
    nodes.find(node => node.id === selectedDeployment?.nodeId)?.sources || [];
  const onDeviceSourceOptions = onDeviceSources.reduce<Record<string, string>>(
    (acc, {sourceId, sourceName}) => {
      acc[sourceId] = sourceName;
      return acc;
    },
    {}
  );
  const externalDeviceOptions = externalStreamingDevices.reduce<Record<string, string>>(
    (acc, curr) => {
      if (curr.type === externalSourceType) {
        acc[curr.id] = curr.name;
      }
      return acc;
    },
    {}
  );

  const inputSourceOptions =
    inputSourceType === 'DEVICE' ? onDeviceSourceOptions : externalDeviceOptions;

  if (field.value) {
    // If the upper input type's are changed its possible that the selected
    // input source no longer applies.  If a selected value exists ensure that it is one of the options
    // if not clear it.
    const matchedOption = Object.keys(inputSourceOptions).find(
      element => element === field.value
    );

    if (!matchedOption) {
      setFieldValue('inputSource', '');
    }
  }

  const handleDeviceDelete = async (id: string) => {
    try {
      const response = await deleteDevice(id);
      if (!response?.errors?.length) {
        await dispatch(fetchExternalStreamingDevices());
        toast.success(
          intl.formatMessage(
            {id: 'test.deleteDeviceSuccessMessage'},
            {
              type: _.startCase(
                externalSourceType === 'FILE'
                  ? intl.formatMessage({id: 'test.form.videoFile'})
                  : intl.formatMessage({id: 'test.form.ipCamera'})
              ),
            }
          )
        );
      }
    } catch (e) {}
  };

  const renderDeleteDevice = (id: string) => {
    return (
      <IconButton
        size="small"
        color="secondary"
        onClick={event => {
          event.stopPropagation();
          setConfirmDeleteDevice({isOpen: true, id});
        }}
      >
        <Clear />
      </IconButton>
    );
  };

  const handleInputSourceChange = () => {};

  return Boolean(
    inputSourceType === 'DEVICE' ||
      (inputSourceType === 'EXTERNAL' &&
        (externalSourceType === 'IP_CAMERA' || externalSourceType === 'FILE'))
  ) ? (
    <>
      <StudioSelect
        className={cn(
          field.value
            ? 'create-prod-deploy__select__selected'
            : 'create-prod-deploy__select__unselected'
        )}
        label={intl.formatMessage({
          id:
            inputSourceType === 'DEVICE'
              ? 'createDeploy.deviceSource'
              : 'createDeploy.externalSource',
        })}
        placeholder="Select a source"
        dataTestId="create-prod-deploy-input-source"
        options={inputSourceOptions}
        disablePlaceholder
        SelectProps={{
          ...field,
          open: setup.srcDev,
          onOpen: () => setSetup({...setup, srcDev: true}),
          onClose: () => setSetup({...setup, srcDev: false}),
          value: field.value === null ? '' : field.value,
        }}
        error={Boolean(meta.error && meta.touched)}
        onChange={handleInputSourceChange}
        helperText={meta.error && meta.touched ? meta.error : ''}
        disabled={disabled}
        renderEndAdornment={inputSourceType === 'EXTERNAL' ? renderDeleteDevice : null}
        required
      />
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage(
          {id: 'test.deleteDeviceConfirmTitle'},
          {
            type: _.startCase(
              externalSourceType === 'FILE'
                ? intl.formatMessage({id: 'test.form.videoFile'})
                : intl.formatMessage({id: 'test.form.ipCamera'})
            ),
          }
        )}
        message={intl.formatMessage(
          {id: 'test.deleteDeviceConfirmMessage'},
          {
            type: _.lowerCase(
              externalSourceType === 'FILE'
                ? intl.formatMessage({id: 'test.form.videoFile'})
                : intl.formatMessage({id: 'test.form.ipCamera'})
            ),
          }
        )}
        submitLabel={intl.formatMessage({
          id: 'form.delete',
        })}
        open={confirmDeleteDevice.isOpen}
        onClose={() => setConfirmDeleteDevice({isOpen: false, id: null})}
        onOk={() => {
          confirmDeleteDevice.id && handleDeviceDelete(confirmDeleteDevice.id);
          setConfirmDeleteDevice({isOpen: false, id: null});
        }}
      />
    </>
  ) : null;
};
